import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArticleCard } from "@/components/article-card";
import { AdSlot, CustomAdSlot } from "@/components/ad-slot";
import { useEffect, useState, lazy, Suspense } from "react";
import { Share2, ArrowLeft, ArrowRight, Tag } from "lucide-react";
import { toast } from "sonner";
import { useSuspenseQuery } from "@tanstack/react-query";
import { fetchArticleBySlug, fetchArticles, fetchSettings } from "@/lib/api";
import { ClientOnly, parseContent, getImageProps, getImageUrl, splitSlug } from "@/lib/utils";
const CommentSection = lazy(() =>
  import("@/components/comment-section").then((m) => ({ default: m.CommentSection })),
);
import { DonationBox } from "@/components/donation-box";

export const Route = createFileRoute("/cerpen/$id/$slug")({
  loader: async ({ params, context: { queryClient } }) => {
    try {
      const { id, slug } = params;
      const fullSlug = id === "p" ? slug : `${slug}-${id}`;
      const [article, settings] = await Promise.all([
        queryClient.ensureQueryData({
          queryKey: ["article", fullSlug],
          queryFn: () => fetchArticleBySlug(fullSlug),
        }),
        queryClient.ensureQueryData({
          queryKey: ["settings"],
          queryFn: fetchSettings,
        }),
      ]);
      return { article, settings };
    } catch (e) {
      throw notFound();
    }
  },
  head: ({ params, loaderData }) => {
    const data = loaderData as any;
    const settings = data?.settings;
    const a = data?.article;
    const siteName = settings?.site_name || "Lihar Aksara";

    if (!a) return { meta: [{ title: `Tidak ditemukan — ${siteName}` }] };

    const title = a.metaTitle || a.title;
    const description = a.metaDescription || a.excerpt;
    const apiBase = import.meta.env.VITE_API_URL || "/api";
    const baseUrl = apiBase.replace("/api", "");
    const imageUrl = a.coverImage
      ? a.coverImage.startsWith("http")
        ? a.coverImage
        : `${baseUrl}${a.coverImage}`
      : "";
    
    const canonicalUrl = `https://liharaksara.web.id/cerpen/${params.id}/${params.slug}`;

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": title,
      "description": description,
      "image": imageUrl,
      "author": {
        "@type": "Person",
        "name": a.author?.name || siteName,
        "url": `https://liharaksara.web.id/penulis/${a.author?.slug}`
      },
      "publisher": {
        "@type": "Organization",
        "name": siteName,
        "logo": {
          "@type": "ImageObject",
          "url": "https://liharaksara.web.id/favicon.ico"
        }
      },
      "datePublished": a.publishedAt || a.createdAt,
      "dateModified": a.updatedAt || a.publishedAt || a.createdAt,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": canonicalUrl
      }
    };

    return {
      meta: [
        { title: `${title} — ${siteName}` },
        { name: "description", content: description },
        { property: "og:title", content: `${title} — ${siteName}` },
        { property: "og:description", content: description },
        { property: "og:image", content: imageUrl },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "article" },
        { property: "og:site_name", content: siteName },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: imageUrl },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl }
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  const { id, slug } = Route.useParams();
  const fullSlug = id === "p" ? slug : `${slug}-${id}`;
  
  const { data: article } = useSuspenseQuery({
    queryKey: ["article", fullSlug],
    queryFn: () => fetchArticleBySlug(fullSlug),
  }) as any;
  
  const { data: allArticles } = useSuspenseQuery({
    queryKey: ["articles"],
    queryFn: fetchArticles,
  }) as any;

  const relatedList = allArticles?.filter((a: any) => a.slug !== article.slug && a.type === "SHORTSTORY").slice(0, 3) || [];

  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setProgress(total > 0 ? Math.min(100, (h.scrollTop / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const date = new Date(article.publishedAt || article.createdAt).toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: article.title, url }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => toast.success("Tautan berhasil disalin!"))
        .catch(() => toast.error("Gagal menyalin tautan."));
    } else {
      toast.error("Fitur salin tidak didukung di browser ini.");
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-[100] bg-muted/20">
        <div 
          className="h-full bg-accent transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <header className="container-prose pt-12 md:pt-20">
        <Link to="/cerpen" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-accent transition-colors mb-10 group"><ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" /> Kembali ke cerpen</Link>
        
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-4">
          {article.categories?.[0]?.name || "Cerpen"}
        </div>
        <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-medium leading-[1.1] tracking-tight text-ink">
          {article.title}
        </h1>
        
        <div className="mt-8 flex items-center justify-between text-xs sm:text-sm text-muted-foreground border-y border-border py-4">
          <div className="flex items-center gap-3">
            <Link 
              to="/penulis/$slug" 
              params={{ slug: article.author?.slug || "" }}
              className="text-foreground font-bold hover:text-accent transition-colors"
            >
              {article.author?.name}
            </Link>
            <span>·</span>
            <span className="font-serif italic">{date}</span>
          </div>
          <button
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors font-bold uppercase tracking-widest text-[10px]"
            onClick={handleShare}
          >
            <Share2 className="w-3.5 h-3.5" /> Bagikan
          </button>
        </div>
      </header>

      <div className="container-prose mt-10">
        <div className="aspect-video w-full rounded-sm overflow-hidden shadow-2xl bg-muted border border-border/50">
          <img
            {...getImageProps(article.coverImage, true)}
            alt={article.title}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
        </div>
      </div>

      <div className="container-prose mt-16 md:mt-24">
        <article className="mx-auto">
            
            
            <div
              className={`article-body ck-content prose prose-slate dark:prose-invert shortstory-content select-none`} onContextMenu={(e) => e.preventDefault()}
              suppressHydrationWarning dangerouslySetInnerHTML={{ __html: parseContent(article.html) }}
            />

            {article.tags && article.tags.length > 0 && (
               <div className="mt-16 pt-8 border-t border-border flex flex-wrap gap-2">
                  {article.tags.map((tag: any) => (
                    <span key={tag.id} className="inline-flex items-center gap-1.5 px-3 py-1 bg-secondary text-[9px] font-bold uppercase tracking-widest rounded-full border border-border text-muted-foreground">
                       <Tag size={12} className=" " />
                       {tag.name}
                    </span>
                  ))}
               </div>
            )}

            <div className="mt-24 md:mt-32">
              <DonationBox />
            </div>

            <div className="mt-24 md:mt-32">
              <Suspense fallback={<div className="h-32 flex items-center justify-center italic text-muted-foreground font-serif">Memuat diskusi...</div>}>
                 <div className="my-12">
                   <AdSlot />
                   <CustomAdSlot />
                 </div>
                 <CommentSection postId={article.id} />
              </Suspense>
            </div>
          </article>

      </div>
    </div>
  );
}
